import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate } from 'react-router-dom';

import authService from './api/authService';
import LoginPage from './components/LoginPage';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import ChangePassword from './components/ChangePassword';
import ProtectedRoute from './components/ProtectedRoute';
import SchedulerPage from './components/SchedulerPage';
import UserManagement from './components/UserManagement'; // <-- RENAMED
import ModuleManagement from './components/ModuleManagement';
import CenterManagement from './components/CenterManagement';
import ProgramManagement from './components/ProgramManagement';
import BatchManagement from './components/BatchManagement';
import Reports from './components/Reports';
import ProfilePage from './components/ProfilePage';
import LeaveManagement from './components/LeaveManagement';
import './App.css';

// The AppLayout component with updated navigation links
const AppLayout = () => {
  const navigate = useNavigate();
  const currentUser = authService.getCurrentUser();
  const isScheduler = currentUser && currentUser.role === 'scheduler';
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = React.useState(false);

  const handleLogout = () => {
    authService.logout();
    navigate('/login', { replace: true });
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const handleProfileClick = () => {
    setIsUserMenuOpen(false);
    navigate('/profile');
  };

  const handleChangePasswordClick = () => {
    setIsUserMenuOpen(false);
    navigate('/change-password');
  };

  const handleLogoutClick = () => {
    setIsUserMenuOpen(false);
    handleLogout();
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (isUserMenuOpen && !event.target.closest('.user-profile-dropdown')) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isUserMenuOpen]);

  return (
    <div className="app-container">
      <header className="modern-navbar">
        <div className="navbar-container">
          {/* Brand Section */}
          <div className="navbar-brand">
            <img
              src="/etasha-logo.png"
              alt="ETASHA Society Logo"
              className="brand-logo"
            />
          </div>

          {/* Desktop Navigation */}
          <nav className="navbar-nav desktop-nav">
            <Link to="/scheduler" className="nav-link">
              <span className="nav-icon">📅</span>
              <span className="nav-text">Scheduler</span>
            </Link>
            <Link to="/leaves" className="nav-link">
              <span className="nav-icon">🏖️</span>
              <span className="nav-text">Leaves</span>
            </Link>
            {isScheduler && (
              <>
                <Link to="/batches" className="nav-link">
                  <span className="nav-icon">📚</span>
                  <span className="nav-text">Batches</span>
                </Link>
                <Link to="/programs" className="nav-link">
                  <span className="nav-icon">🎯</span>
                  <span className="nav-text">Programs</span>
                </Link>
                <Link to="/modules" className="nav-link">
                  <span className="nav-icon">🎓</span>
                  <span className="nav-text">Modules</span>
                </Link>
                <Link to="/trainers" className="nav-link">
                  <span className="nav-icon">👩‍🏫</span>
                  <span className="nav-text">Facilitators</span>
                </Link>
                <Link to="/centers" className="nav-link">
                  <span className="nav-icon">🏢</span>
                  <span className="nav-text">Centers</span>
                </Link>
                <Link to="/reports" className="nav-link">
                  <span className="nav-icon">📊</span>
                  <span className="nav-text">Dashboard</span>
                </Link>
              </>
            )}
          </nav>

          {/* User Profile & Actions */}
          <div className="navbar-actions">
            {currentUser && (
              <div className="user-profile-dropdown">
                <button
                  className="user-profile-trigger"
                  onClick={toggleUserMenu}
                  aria-label="User menu"
                >
                  <div className="user-avatar">
                    {currentUser.name ? currentUser.name.charAt(0).toUpperCase() : '👤'}
                  </div>
                  <div className="user-info">
                    <span className="user-name">{currentUser.name || 'User'}</span>
                    <span className="user-role">{currentUser.role}</span>
                  </div>
                  <span className={`dropdown-arrow ${isUserMenuOpen ? 'open' : ''}`}>▼</span>
                </button>

                {isUserMenuOpen && (
                  <div className="user-dropdown-menu">
                    <button className="dropdown-item" onClick={handleProfileClick}>
                      <span className="dropdown-icon">👤</span>
                      <span className="dropdown-text">My Profile</span>
                    </button>
                    <button className="dropdown-item" onClick={handleChangePasswordClick}>
                      <span className="dropdown-icon">🔒</span>
                      <span className="dropdown-text">Change Password</span>
                    </button>
                    <div className="dropdown-divider"></div>
                    <button className="dropdown-item logout" onClick={handleLogoutClick}>
                      <span className="dropdown-icon">🚪</span>
                      <span className="dropdown-text">Logout</span>
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Mobile Menu Toggle */}
            <button
              className="mobile-menu-toggle"
              onClick={toggleMobileMenu}
              aria-label="Toggle mobile menu"
            >
              <span className={`hamburger ${isMobileMenuOpen ? 'open' : ''}`}>
                <span></span>
                <span></span>
                <span></span>
              </span>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <nav className={`mobile-nav ${isMobileMenuOpen ? 'open' : ''}`}>
          <Link to="/scheduler" className="mobile-nav-link" onClick={() => setIsMobileMenuOpen(false)}>
            <span className="nav-icon">📅</span>
            <span className="nav-text">Scheduler</span>
          </Link>
          <Link to="/leaves" className="mobile-nav-link" onClick={() => setIsMobileMenuOpen(false)}>
            <span className="nav-icon">🏖️</span>
            <span className="nav-text">Leaves</span>
          </Link>
          {isScheduler && (
            <>
              <Link to="/batches" className="mobile-nav-link" onClick={() => setIsMobileMenuOpen(false)}>
                <span className="nav-icon">📚</span>
                <span className="nav-text">Batches</span>
              </Link>
              <Link to="/programs" className="mobile-nav-link" onClick={() => setIsMobileMenuOpen(false)}>
                <span className="nav-icon">🎯</span>
                <span className="nav-text">Programs</span>
              </Link>
              <Link to="/modules" className="mobile-nav-link" onClick={() => setIsMobileMenuOpen(false)}>
                <span className="nav-icon">🎓</span>
                <span className="nav-text">Modules</span>
              </Link>
              <Link to="/trainers" className="mobile-nav-link" onClick={() => setIsMobileMenuOpen(false)}>
                <span className="nav-icon">👩‍🏫</span>
                <span className="nav-text">Facilitators</span>
              </Link>
              <Link to="/centers" className="mobile-nav-link" onClick={() => setIsMobileMenuOpen(false)}>
                <span className="nav-icon">🏢</span>
                <span className="nav-text">Centers</span>
              </Link>
              <Link to="/reports" className="mobile-nav-link" onClick={() => setIsMobileMenuOpen(false)}>
                <span className="nav-icon">📊</span>
                <span className="nav-text">Dashboard</span>
              </Link>
            </>
          )}
          <Link to="/profile" className="mobile-nav-link" onClick={() => setIsMobileMenuOpen(false)}>
            <span className="nav-icon">👤</span>
            <span className="nav-text">My Profile</span>
          </Link>
        </nav>
      </header>
      <main className="app-content">
        <Routes>
          <Route path="/scheduler" element={<SchedulerPage />} />
          <Route path="/leaves" element={<LeaveManagement />} />
          <Route path="/trainers" element={<UserManagement />} /> {/* <-- CORRECTED */}
          <Route path="/batches" element={<BatchManagement />} />
          <Route path="/programs" element={<ProgramManagement />} />
          <Route path="/modules" element={<ModuleManagement />} />
          <Route path="/centers" element={<CenterManagement />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/change-password" element={<ChangePassword />} />
          <Route index element={<SchedulerPage />} />
        </Routes>
      </main>
    </div>
  );
};

// Main App component with password reset routes
function App() {
  return (
    <Router>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route element={<ProtectedRoute />}>
          <Route path="/*" element={<AppLayout />} />
        </Route>
      </Routes>
    </Router>
  );
}

export default App;